import React from 'react';
import { makeStyles } from '@material-ui/core';
import {
  SignInPage as BackstageSignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import {
  SignInPageProps,
  useApi,
  configApiRef,
} from '@backstage/core-plugin-api';
import { jumpcloudAuthApiRef } from '../../apis';

const jumpcloudProvider: SignInProviderConfig = {
  id: 'jumpcloud-oidc',
  title: 'JumpCloud',
  message:
    'Sign in using JumpCloud. Before signing in, please ensure you have received an email from the DevOps team with a link to sign in.',
  apiRef: jumpcloudAuthApiRef,
};

/**
 * Wraps Backstage's SignInPage to present a centered, branded auth screen.
 *
 * SignInPage is an app component (not a routable extension), so wrapping is
 * safe. We keep Backstage's provider handshake untouched and only restyle:
 * the provider card is centered in the viewport below the branded header and
 * given a premium surface, and the sign-in action becomes a full-width brand
 * CTA. Selectors key off Backstage's stable makeStyles class prefixes
 * (BackstageContent-root, BackstageSignInPage-item, BackstageInfoCard-header),
 * matched via attribute selectors since the JSS suffixes are not stable.
 */
const useStyles = makeStyles(theme => ({
  root: {
    // The wrapper only scopes the CSS below; it must add no layout box.
    display: 'contents',

    // Center the provider card in the space below the branded header, and lay
    // a subtle brand glow behind it.
    '& [class*="BackstageContent-root"]': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 72px)',
      background:
        theme.palette.type === 'dark'
          ? 'radial-gradient(1100px 480px at 50% -8%, rgba(44,201,192,0.10), transparent 68%)'
          : 'radial-gradient(1100px 480px at 50% -8%, rgba(0,130,141,0.08), transparent 68%)',
    },

    // Widen the provider column beyond the default 400px cap.
    '& [class*="BackstageSignInPage-item"]': {
      maxWidth: 460,
    },

    // Premium card surface.
    '& [class*="MuiCard-root"]': {
      width: '100%',
      borderRadius: 20,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow:
        theme.palette.type === 'dark'
          ? '0 24px 60px -20px rgba(0,0,0,0.7)'
          : '0 24px 60px -24px rgba(15,23,42,0.28)',
      overflow: 'hidden',
    },

    // Centered, larger provider title.
    '& [class*="BackstageInfoCard-header"]': {
      textAlign: 'center',
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(1.5),
    },
    '& [class*="MuiCardHeader-title"]': {
      fontSize: '1.5rem',
      fontWeight: 700,
    },

    // Message copy.
    '& [class*="MuiCardContent-root"]': {
      textAlign: 'center',
      padding: theme.spacing(1, 4, 2),
      color: theme.palette.text.secondary,
      lineHeight: 1.6,
    },

    // Full-width, filled brand CTA in place of the default outlined button.
    '& [class*="MuiCardActions-root"]': {
      padding: theme.spacing(2, 4, 4),
    },
    '& [class*="MuiCardActions-root"] [class*="MuiButton-root"]': {
      width: '100%',
      padding: theme.spacing(1.25, 3),
      borderRadius: 12,
      fontSize: '0.95rem',
      fontWeight: 600,
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      border: 'none',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

export const CustomSignInPage = (props: SignInPageProps) => {
  const classes = useStyles();
  const config = useApi(configApiRef);

  // In dev, sign-in switches to guest-only (no JumpCloud backend needed
  // locally). `auth.environment: development` is only set in
  // app-config.local.yaml (git-ignored) — staging + prod both use
  // `staging`, so the guest button never leaks into deployed envs.
  const isDev = config.getOptionalString('auth.environment') === 'development';
  // Explicit union type — TS widens ['guest'] | [jumpcloudProvider] to
  // string[] | SignInProviderConfig[], which fails IdentityProviders.
  const providers: (SignInProviderConfig | 'guest')[] = isDev
    ? ['guest']
    : [jumpcloudProvider];

  return (
    <div className={classes.root}>
      <BackstageSignInPage
        {...props}
        auto
        align="center"
        providers={providers}
      />
    </div>
  );
};
